"use client";

import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import GlobalForm from "@/components/forms/GlobalForm";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

/* ---------------- Data ---------------- */
const examsdesk = [
  {
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-one.webp",
    label: "Financial Acc. & Reporting",
    src: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/far_new.webp",
  },
  {
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-two.webp",
    label: "Auditing and Attestation",
    src: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/aud_new.webp",
  },
  {
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-three.webp",
    label: "Regulation",
    src: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/reg_new.webp",
  },
  {
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-four.webp",
    label: "Business Analysis & Reporting",
    src: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/bar_new.webp",
  },
];

const examsmob = [
  {
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-one.webp",
    label: "Financial Accounting\n & Reporting",
    src: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/far_new.webp?tr=w-240,h-130,q-80",
  },
  {
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-two.webp",
    label: "Auditing and\n Attestation",
    src: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/aud_new.webp?tr=w-240,h-130,q-80",
  },
  {
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-three.webp",
    label: "Regulation",
    src: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/reg_new.webp?tr=w-240,h-130,q-80",
  },
  {
    img: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/b-four.webp",
    label: "Business Analysis\n & Reporting",
    src: "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/bar_new.webp?tr=w-240,h-130,q-80",
  },
];

/* ---------------- Component ---------------- */
const CpaEquivalent = () => {
  const pathname = usePathname();
  const [isformPopupOpen, setformPopupOpen] = useState(false);

  // Hide button on specific URL
  const shouldHideButton =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india";

  const openformPopup = () => {
    sessionStorage.setItem("modal-open", "true");
    setformPopupOpen(true);
  };
  const closeformPopup = () => {
    sessionStorage.removeItem("modal-open");
    setformPopupOpen(false);
  };

  // Embla carousel (MOBILE image row)
  const autoplayRef = useRef(
    Autoplay({
      delay: 2200,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      stopOnFocusIn: false,
    }),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      dragFree: true,
      containScroll: "trimSnaps",
      slidesToScroll: 1,
      skipSnaps: false,
    },
    [autoplayRef.current],
  );

  useEffect(() => {
    if (emblaApi && autoplayRef.current) autoplayRef.current.play();
  }, [emblaApi]);

  return (
    <>
      <div className="w-full bg-[#061A2F] relative mx-auto">
        <div className="main_container mx-auto px-5 md:pt-12 md:pb-12 pt-4 pb-5">
          {/* Heading */}
          <h2 className="text-center font-semibold leading-tight">
            <span className="relative inline-block text-[20px] md:text-[32px] tracking-[-0.01em]">
              <span className="bg-[linear-gradient(91deg,#39F_81.57%,#FFBA08_106.77%)] bg-clip-text text-transparent">
                CPA = U.S.
              </span>
              <span className="text-white"> equivalent of CA</span>
              <span
                aria-hidden
                className="pointer-events-none absolute left-[10px] right-[55px] -bottom-[4px] h-[2px] bg-[#FFB801] rounded-[5px]"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute -bottom-[4px] right-[25px] h-[2px] w-6 bg-[#FFB801] rounded-[5px]"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute -bottom-[4px] right-[4px] h-[2px] w-4 bg-[#FFB801] rounded-[5px]"
              />
            </span>
          </h2>
          {!shouldHideButton && (
            <p className="md:mt-6 mt-5 text-center text-white text-sm font-medium tracking-[0.56px]">
              Only 4 Exams &amp; 12 Months Timeline
            </p>
          )}

          {shouldHideButton && (
            <p className="md:mt-6 mt-5 text-center text-white text-sm font-medium tracking-[0.56px]">
              4 Exams | 12 Months
            </p>
          )}

          {/* Desktop block */}
          <div
            className="rounded-[16px] border border-[#39F] backdrop-blur-[4px] md:p-5 mt-4 md:block hidden"
            style={{
              background:
                "linear-gradient(0deg, rgba(51,153,255,0.10) 0%, rgba(51,153,255,0.10) 100%), rgba(255,255,255,0.04)",
            }}
          >
            <h3 className="text-white md:text-2xl font-medium text-center">
              Learn from VARUN JAIN, World’s favourite CPA Instructor
            </h3>
            <div className="flex justify-center items-center">
              <p className="text-white md:text-lg font-medium text-center">
                with 100MM+ YouTube views
              </p>
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/youtube.webp"
                alt="Youtube Icon"
                className="ml-2"
                width={20}
                height={20}
                loading="lazy"
              />
            </div>
            <div className="grid grid-cols-4 gap-2 md:gap-5 w-full md:mt-6 relative">
              {examsdesk?.map((item, index) => (
                <div key={index} className="flex flex-col gap-2">
                  <div className="flex gap-2 items-center">
                    <Image
                      src={item.img}
                      alt="exam"
                      width={24}
                      height={24}
                      loading="lazy"
                    />
                    <p className="text-white font-medium text-xs md:text-base leading-[1.2] whitespace-pre-line">
                      {item.label}
                    </p>
                  </div>
                  <div className="w-full rounded-[8px] overflow-hidden md:mt-3">
                    <Image
                      src={item.src}
                      alt="Video"
                      className="rounded-[8px] w-full h-full object-cover min-h-[140px]"
                      width={250}
                      height={140}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
              {!shouldHideButton && (
                <p
                  className="rounded-[8px] border border-[#39F] backdrop-blur-[4px] md:text-base font-semibold text-black text-center py-[0.5px] px-5 absolute left-1/2 -translate-x-1/2 bottom-[-38px] md:block hidden"
                  style={{
                    background:
                      "linear-gradient(0deg, #FFB801 0%, #FFB801 100%), #FFF",
                  }}
                >
                  80%+ CPAs in India are Miles Alumni
                </p>
              )}
            </div>
          </div>

          {/* Mobile block */}
          <div
            className="rounded-[16px] border border-[#39F] backdrop-blur-[4px] p-3 mt-4 md:hidden block"
            style={{
              background:
                "linear-gradient(0deg, rgba(51,153,255,0.10) 0%, rgba(51,153,255,0.10) 100%), rgba(255,255,255,0.04)",
            }}
          >
            <h3 className="text-white text-center leading-6 text-sm">
              <span className="text-[#D4D4D4]">Learn from</span>{" "}
              <span className="block font-bold text-[32px]">VARUN JAIN</span>{" "}
              World’s favourite CPA Instructor
            </h3>
            <div className="flex justify-center items-center mt-1">
              <p className="text-white text-xs font-medium text-center">
                with 100MM+ YouTube views
              </p>
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit-new/youtube.webp"
                alt="Youtube Icon"
                className="ml-2"
                width={20}
                height={20}
                loading="lazy"
              />
            </div>

            {/* Top text rows */}
            <div className="grid grid-cols-2 gap-2 w-full mt-3">
              {examsmob?.map((item, index) => (
                <div key={index} className="flex flex-col gap-2 mt-2">
                  <div className="flex gap-2 items-center">
                    <Image
                      src={item.img}
                      alt="exam"
                      width={24}
                      height={24}
                      loading="lazy"
                    />
                    <p className="text-white font-medium text-xs leading-[1.2] whitespace-pre-line">
                      {item.label}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Image Carousel */}
            <div className="mt-3">
              <div ref={emblaRef} className="embla overflow-hidden">
                <div className="embla__container flex -ml-1">
                  {examsmob.map((item, index) => (
                    <div
                      key={index}
                      className="embla__slide flex-[0_0_60%] min-w-0 pr-2"
                    >
                      <Image
                        src={item.src}
                        alt="Video"
                        className="rounded-[8px] w-full h-full object-cover min-h-[85px]"
                        width={152}
                        height={85}
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {!shouldHideButton && (
            <p
              className="rounded-[8px] border border-[#39F] backdrop-blur-[4px] text-sm font-semibold text-black text-center py-[2px] px-5 md:hidden block my-5"
              style={{
                background:
                  "linear-gradient(0deg, #FFB801 0%, #FFB801 100%), #FFF",
              }}
            >
              80%+ CPAs in India are Miles Alumni
            </p>
          )}

          {/* CTA - Hidden on /best-us-cpa-admission-online-course-fees-in-india */}
          {!shouldHideButton && (
            <button
              className="block mx-auto bg-[#2A85FF] rounded-[12px_12px_12px_0px] text-white font-bold py-1 px-4 md:h-[32px] text-sm text-center md:mt-10 mt-5 md:w-[214px]"
              onClick={openformPopup}
            >
              Join Webinar to Know More
            </button>
          )}
        </div>
      </div>

      {/* Form modal */}
      {isformPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-auto mx-auto border border-white p-4 w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.30) 0%, rgba(51,153,255,0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={closeformPopup}
              className="mt-2 text-white px-3 text-lg py-1 rounded absolute right-[10px] top-[0px] md:top-[-6px] z-[9999]"
            >
              ✕
            </button>
            <Image
              src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
              alt="star group"
              width={34}
              height={38}
              className="absolute top-3 left-[-8px]"
              loading="lazy"
            />
            <GlobalForm
              course="CPA"
              courseId={1}
              formHeading={FormHeading.WORLD_FAVORITE_CMA_CPA_INSTRUCTOR}
              formType="page"
              isAStepperForm
              pageType="accounting page"
              subCategory={FinanceAndAccountingSubcategory.CPA}
              submitButtonName="Register for Free Webinar"
              successMessage={SuccessMessageType.ACCOUNTING}
              verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
              darkBackground
            />
          </div>
        </div>
      )}
    </>
  );
};

export default CpaEquivalent;
